import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

function SearchIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function TvIllustration() {
  return (
    <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-2xl border border-slate-700/80 bg-gradient-to-b from-slate-800/80 to-slate-900/90 shadow-inner ring-1 ring-white/5">
      <svg className="h-11 w-11 text-brand-400/90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.25}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.5l.65 1.73h3.2l.65-1.73M6 8.5h12a2 2 0 012 2v7a2 2 0 01-2 2H6a2 2 0 01-2-2v-7a2 2 0 012-2z" />
        <path strokeLinecap="round" d="M9 14.5h6" />
      </svg>
    </div>
  );
}

const ChannelList = ({ channels = [], setSelectedChannel, selectedCountry, searchTerm, setSearchTerm }) => {
  const [localSearch, setLocalSearch] = useState("");
  const nav = useNavigate();
  const { language } = useLanguage();
  const isFr = language === "fr";
  const t = (frText, enText) => (isFr ? frText : enText);

  useEffect(() => {
    if (searchTerm !== undefined && searchTerm !== null) {
      setLocalSearch(searchTerm);
    }
  }, [searchTerm]);

  const filteredChannels = useMemo(() => {
    return channels.filter((channel) =>
      channel.name.toLowerCase().includes(localSearch.toLowerCase())
    );
  }, [channels, localSearch]);

  const onSearchChange = (e) => {
    const v = e.target.value;
    setLocalSearch(v);
    if (setSearchTerm) setSearchTerm(v);
  };

  const clearFilter = () => {
    setLocalSearch("");
    if (setSearchTerm) setSearchTerm("");
  };

  const listEmpty = channels.length === 0;
  const filterEmpty = !listEmpty && filteredChannels.length === 0;

  return (
    <div className="min-h-screen bg-slate-950 bg-mesh-gradient pb-[calc(env(safe-area-inset-bottom)+4.5rem)] sm:pb-24">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,rgba(2,6,23,0.5)_100%)]" />

      <header className="relative z-10 border-b border-slate-800/80 bg-slate-950/85 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 sm:py-5 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() => nav(-1)}
                className="group mb-3 inline-flex items-center gap-2 rounded-lg border border-transparent px-0 py-1.5 text-xs font-medium text-slate-400 transition hover:text-brand-300"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-700/80 bg-slate-900/60 text-slate-500 transition group-hover:border-slate-600 group-hover:text-brand-400">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </span>
                {t("Retour", "Back")}
              </button>
              <div className="flex flex-wrap items-end gap-3 gap-y-2">
                <h1 className="text-xl font-bold tracking-tight text-white sm:text-4xl">{t("Chaînes", "Channels")}</h1>
                {selectedCountry ? (
                  <span className="inline-flex items-center rounded-full border border-slate-700/90 bg-slate-900/70 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-300 ring-1 ring-white/5">
                    {t("Région", "Region")} <span className="ml-1.5 font-mono text-brand-300">{String(selectedCountry).toUpperCase()}</span>
                  </span>
                ) : null}
              </div>
              <p className="mt-1.5 max-w-xl text-xs leading-relaxed text-slate-500 sm:mt-2 sm:text-sm">
                {listEmpty
                  ? t("Cette liste est vide. Revenez à l'accueil ou ouvrez l'explorateur de pays pour charger des chaînes.", "This list is empty. Return home or open the country browser to load channels.")
                  : t("Sélectionnez une chaîne pour ouvrir le lecteur. Utilisez le champ ci-dessous pour filtrer la liste par nom.", "Select a channel to open the player. Use the field below to narrow the list by name.")}
              </p>
            </div>

            <div className="w-full shrink-0 lg:max-w-md">
              <label htmlFor="channel-filter" className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-slate-500 sm:mb-1.5 sm:text-xs">
                {t("Filtrer par nom", "Filter by name")}
              </label>
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  id="channel-filter"
                  type="search"
                  placeholder={t("Saisissez pour filtrer…", "Type to filter…")}
                  value={localSearch}
                  onChange={onSearchChange}
                  className="ui-input h-10 rounded-lg pl-10 pr-10 text-sm sm:h-auto sm:rounded-xl"
                  autoComplete="off"
                />
                {localSearch ? (
                  <button
                    type="button"
                    onClick={clearFilter}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                    aria-label={t("Effacer le filtre", "Clear filter")}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {!listEmpty ? (
            <>
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-800/60 pt-3 text-xs text-slate-500 sm:mt-6 sm:pt-5 sm:text-sm">
                <span className="font-medium text-slate-400">
                  <span className="tabular-nums text-white">{filteredChannels.length}</span>
                  {localSearch ? (
                    <> {t("sur", "of")} <span className="tabular-nums text-slate-300">{channels.length}</span> {t("affichées", "shown")}</>
                  ) : (
                    <> {t("dans cette liste", "in this list")}</>
                  )}
                </span>
                {localSearch ? (
                  <span className="hidden h-1 w-1 rounded-full bg-slate-600 sm:inline" aria-hidden />
                ) : null}
                {localSearch ? (
                  <button type="button" onClick={clearFilter} className="text-xs font-semibold text-brand-400 transition hover:text-brand-300">
                    {t("Effacer la recherche", "Clear search")}
                  </button>
                ) : null}
              </div>
              <LanguageSwitcher />
            </>
          ) : null}
        </div>
      </header>

      <div className="relative z-10 mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-10 lg:px-8">
        {listEmpty ? (
          <div className="mx-auto max-w-lg">
            <div className="relative overflow-hidden rounded-3xl border border-slate-800/80 bg-slate-900/40 p-10 text-center shadow-panel ring-1 ring-white/5">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-500/10 blur-3xl" />
              <TvIllustration />
              <h2 className="mt-8 text-lg font-semibold text-white">{t("Aucune chaîne dans cette liste", "No channels in this list")}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">
                {t("Votre sélection n'est peut-être pas encore chargée, ou cette région n'a aucun flux indexé. Essayez un autre pays ou une autre catégorie depuis l'accueil.", 'Your selection may not have loaded yet, or this region has no indexed streams. Try another country or category from the home page.')}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <button type="button" onClick={() => nav("/")} className="ui-btn-secondary min-h-[44px] px-6">
                  {t("Accueil", "Home")}
                </button>
                <button type="button" onClick={() => nav("/all-countries")} className="ui-btn-primary min-h-[44px] px-6">
                  Explorateur de pays
                </button>
              </div>
            </div>
          </div>
        ) : filterEmpty ? (
          <div className="mx-auto max-w-lg">
            <div className="relative overflow-hidden rounded-3xl border border-slate-800/80 bg-slate-900/40 p-10 text-center shadow-panel ring-1 ring-white/5">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(42,158,244,0.08),transparent_55%)]" />
              <div className="relative">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-700/80 bg-slate-800/50 text-slate-400">
                  <SearchIcon className="h-6 w-6" />
                </div>
                <h2 className="mt-6 text-lg font-semibold text-white">{t("Aucune chaîne ne correspond à ce filtre", "No channels match this filter")}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                  {t(`Aucun élément de la liste actuelle ne correspond à “${localSearch}”. Effacez la recherche ou essayez d'autres mots-clés.`, `Nothing in the current list matches “${localSearch}”. Clear the search or try different keywords.`)}
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                  <button type="button" onClick={clearFilter} className="ui-btn-primary min-h-[44px] px-6">
                    Effacer le filtre
                  </button>
                  <button type="button" onClick={() => nav("/")} className="ui-btn-secondary min-h-[44px] px-6">
                  {t("Accueil", "Home")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filteredChannels.map((channel, index) => (
              <li key={channel.name || index}>
                <button
                  type="button"
                  className="group flex w-full flex-col rounded-xl border border-slate-800/80 bg-slate-900/35 p-4 text-left shadow-lg ring-1 ring-white/[0.03] transition hover:border-brand-500/25 hover:bg-slate-900/60 hover:shadow-glow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500/50 sm:rounded-2xl sm:p-5"
                  onClick={() => {
                    setSelectedChannel(channel);
                    nav("/player");
                  }}
                >
                  <div className="flex gap-3 sm:gap-4">
                    <div className="relative shrink-0">
                      <div className="absolute -inset-0.5 rounded-xl bg-gradient-to-br from-brand-500/0 to-indigo-500/0 opacity-0 blur transition group-hover:from-brand-500/20 group-hover:to-indigo-500/15 group-hover:opacity-100" />
                      {channel.logo ? (
                        <img
                          src={channel.logo}
                          alt=""
                          className="relative h-14 w-14 rounded-lg bg-slate-950 object-contain p-1 ring-1 ring-slate-700/80 sm:h-16 sm:w-16 sm:rounded-xl sm:p-1.5"
                        />
                      ) : (
                        <div className="relative flex h-14 w-14 items-center justify-center rounded-lg bg-slate-800 ring-1 ring-slate-700/80 sm:h-16 sm:w-16 sm:rounded-xl">
                          <span className="text-sm font-bold text-brand-400">TV</span>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 pt-0.5">
                      <h3 className="text-[15px] font-semibold leading-snug text-white group-hover:text-brand-100 sm:text-base">{channel.name}</h3>
                      {channel.description ? (
                        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-500">{channel.description}</p>
                      ) : (
                        <p className="mt-2 text-xs font-medium uppercase tracking-wider text-slate-600">Ouvrir le flux</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-slate-800/60 pt-3 text-xs font-medium text-slate-500">
                    <span>Lire dans le lecteur</span>
                    <span className="text-brand-400/80 transition group-hover:text-brand-300" aria-hidden>
                      →
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ChannelList;
